/**
 * Admin Feature - Shared Types and Helpers
 *
 * Extracted from AnalyticsView.tsx to share across admin views:
 * - BillingView (export transactions, add matter)
 * - AdminDashboardView (usage metrics)
 * - AdminSupportView (support inbox)
 */

import { format } from "date-fns";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type Professional = {
  prof_id: string;
  full_name: string;
  email: string;
};

export type UsageRecord = {
  prof_id: string;
  matter: string; // actually full_name
  client: string; // actually email
  year: number;
  month: number;
  total_hours: number;
  exported_hours: number;
  last_export_time: string | null;
  total_entries: number;
  unexported_entries: number;
};

export type AttorneyMetrics = {
  prof_id: string;
  full_name: string;
  email: string;
  unexported_hours: number;
  unexported_count: number;
  exported_hours: number;
  exported_count: number;
  last_export_date: Date | null;
  days_since_export: number | null;
  export_rate: number;
};

export type FeedbackItem = {
  id: number;
  created_at: string;
  user_email: string;
  user_name: string;
  firm_domain: string;
  issue_type: "bug" | "feature" | "question";
  what_happened: string | null;
  expected_behavior: string | null;
  bug_anything_else: string | null;
  feature_description: string | null;
  current_workaround: string | null;
  feature_anything_else: string | null;
  question: string | null;
  screenshots: string | null;
  status: string;
};

export type MatterFormValues = {
  matter_id: string;
  client: string;
  description: string;
  matter: string;
};

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

/** Test accounts to filter out from production display */
export const TEST_ACCOUNTS = [
  "Dayn Reoh",
  "Test Account",
  "Kristin Baldwin",
  "Whitney Blocker",
  "Leah Paul",
  "Rachel Borchers",
];

/** Hourglass internal emails to hide from support inbox (unless viewer is internal) */
export const HOURGLASS_INTERNAL_EMAILS = [
  "dayn@hourglass.law",
  "demo@hourglass.law",
  "tj@hourglass.law",
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/**
 * Convert a timeframe string to a date range.
 * Returns null for "all" (no filter).
 */
export function getDateRangeFromTimeframe(
  timeframe: string
): { startDate: string; endDate: string } | null {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = now;

  switch (timeframe) {
    case "today":
      startDate = now;
      break;
    case "yesterday":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 1);
      endDate = new Date(startDate);
      break;
    case "3days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 2);
      break;
    case "7days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 6);
      break;
    case "thismonth":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "30days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 29);
      break;
    case "90days":
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 89);
      break;
    case "1year":
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    case "all":
      return null;
    default:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 6);
  }

  return {
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
  };
}

/**
 * Get summary text for a feedback item based on its type.
 */
export function getFeedbackSummary(item: FeedbackItem): string {
  switch (item.issue_type) {
    case "bug":
      return item.what_happened || "No description";
    case "feature":
      return item.feature_description || "No description";
    case "question":
      return item.question || "No question";
    default:
      return "Unknown type";
  }
}

/**
 * Parse screenshots JSON string to array of URLs.
 */
export function parseScreenshots(screenshotsJson: string | null): string[] {
  if (!screenshotsJson) return [];
  try {
    return JSON.parse(screenshotsJson);
  } catch {
    return [];
  }
}

/**
 * Calculate attorney metrics from usage data.
 */
export function calculateAttorneyMetrics(
  usageData: UsageRecord[],
  professionals: Professional[],
  globalExportTimes: Record<string, string | null>
): AttorneyMetrics[] {
  const metricsMap = new Map<string, AttorneyMetrics>();

  usageData.forEach((record) => {
    const existing = metricsMap.get(record.prof_id);
    const unexportedHours = record.total_hours - record.exported_hours;
    const exportedCount = record.total_entries - record.unexported_entries;

    if (existing) {
      existing.unexported_hours += unexportedHours;
      existing.unexported_count += record.unexported_entries;
      existing.exported_hours += record.exported_hours;
      existing.exported_count += exportedCount;
      if (record.last_export_time) {
        const exportDate = new Date(record.last_export_time);
        if (!existing.last_export_date || exportDate > existing.last_export_date) {
          existing.last_export_date = exportDate;
        }
      }
    } else {
      metricsMap.set(record.prof_id, {
        prof_id: record.prof_id,
        full_name: record.matter, // 'matter' field contains full_name
        email: record.client, // 'client' field contains email
        unexported_hours: unexportedHours,
        unexported_count: record.unexported_entries,
        exported_hours: record.exported_hours,
        exported_count: exportedCount,
        last_export_date: record.last_export_time
          ? new Date(record.last_export_time)
          : null,
        days_since_export: null,
        export_rate: 0,
      });
    }
  });

  // Add professionals with 0 hours (not in usageData for this time range)
  professionals.forEach((prof) => {
    if (!metricsMap.has(prof.prof_id)) {
      metricsMap.set(prof.prof_id, {
        prof_id: prof.prof_id,
        full_name: prof.full_name,
        email: prof.email,
        unexported_hours: 0,
        unexported_count: 0,
        exported_hours: 0,
        exported_count: 0,
        last_export_date: null,
        days_since_export: null,
        export_rate: 0,
      });
    }
  });

  // Calculate days since export and export rate
  const now = new Date();
  metricsMap.forEach((metrics) => {
    const globalLastExportStr = globalExportTimes[metrics.prof_id];
    if (globalLastExportStr) {
      const globalLastExport = new Date(globalLastExportStr);
      metrics.days_since_export = Math.floor(
        (now.getTime() - globalLastExport.getTime()) / (1000 * 60 * 60 * 24)
      );
      metrics.last_export_date = globalLastExport;
    }
    const totalEntries = metrics.exported_count + metrics.unexported_count;
    metrics.export_rate =
      totalEntries > 0
        ? Math.round((metrics.exported_count / totalEntries) * 100)
        : 0;
    metrics.unexported_hours = Math.round(metrics.unexported_hours * 10) / 10;
    metrics.exported_hours = Math.round(metrics.exported_hours * 10) / 10;
  });

  return Array.from(metricsMap.values()).sort(
    (a, b) => b.unexported_hours - a.unexported_hours
  );
}

/**
 * Validate matter form fields.
 * Returns an object with field names as keys and error messages as values.
 */
export function validateMatterForm(
  values: MatterFormValues
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!values.matter_id.trim()) {
    errors.matter_id = "Matter ID is required";
  }
  if (!values.client.trim()) {
    errors.client = "Client is required";
  }
  if (!values.description.trim()) {
    errors.description = "Description is required";
  }
  if (!values.matter.trim()) {
    errors.matter = "Matter name is required";
  }

  return errors;
}
